import React from 'react';
import { SectionWrapper } from './ui/SectionWrapper';
import { AlertTriangle, XCircle, AlertOctagon, Star } from 'lucide-react';
import { motion } from 'framer-motion';
export function ProblemSection() {
  const testimonials = [{
    quote: 'No tenía idea de los riesgos reales. Este sistema me abrió los ojos.',
    author: 'Laura M.',
    role: 'Mamá de 2 hijos'
  }, {
    quote: 'Pude configurar todo el celular de mi hijo en una tarde.',
    author: 'Diego R.',
    role: 'Padre de adolescente'
  }, {
    quote: 'Lo mejor es que explica todo sin tecnicismos. Muy claro.',
    author: 'Carolina S.',
    role: 'Mamá primeriza digital'
  }, {
    quote: 'Después de usar el sistema, tuve la mejor charla con mi hija sobre redes.',
    author: 'Martín P.',
    role: 'Padre de 3'
  }, {
    quote: 'Ojalá hubiera tenido esto antes. Es exactamente lo que necesitaba.',
    author: 'Valeria G.',
    role: 'Mamá de pre-adolescente'
  }, {
    quote: 'Me dio herramientas concretas, no solo teoría.',
    author: 'Fernando L.',
    role: 'Padre preocupado'
  }];
  return <>
      <SectionWrapper className="text-center px-0 md:px-4" data-id="element-149">
        <h2 className="text-2xl md:text-4xl font-extrabold mb-6 md:mb-12 text-white px-4 md:px-0" data-id="element-150">
          LO QUE DICEN OTROS PADRES
        </h2>

        <div className="flex flex-col gap-4 max-w-2xl mx-auto px-4 md:px-0" data-id="element-151">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-white shadow-lg rounded-2xl p-5 md:p-6 text-left flex flex-col relative border border-slate-100" data-id="element-152">
              <div className="flex gap-1 mb-3" data-id="element-153">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-154" />)}
              </div>
              <p className="text-base md:text-lg italic text-brand-text font-medium leading-relaxed mb-4" data-id="element-155">
                "{t.quote}"
              </p>
              <div className="flex flex-col items-end w-full mt-auto pt-3 border-t border-slate-50" data-id="element-156">
                <p className="font-bold text-brand-navy text-sm md:text-base" data-id="element-157">
                  {t.author}
                </p>
                <p className="text-xs md:text-sm text-brand-textLight" data-id="element-158">
                  {t.role}
                </p>
              </div>
            </motion.div>)}
        </div>
      </SectionWrapper>

      <SectionWrapper data-id="element-159">
        <div className="flex flex-col items-center text-center mb-8 md:mb-12" data-id="element-160">
          <div className="bg-red-500/15 text-red-500 p-3 md:p-4 rounded-full mb-4 md:mb-6" data-id="element-161">
            <AlertTriangle className="w-8 h-8 md:w-10 md:h-10" data-id="element-162" />
          </div>
          <h2 className="text-2xl md:text-5xl font-extrabold mb-4 md:mb-6 text-white" data-id="element-163">
            ¿CUÁNTOS DÍAS MÁS VAS A DEJARLO ASÍ?
          </h2>
          <p className="text-base md:text-xl text-slate-400 max-w-2xl" data-id="element-164">
            Sos padre. Querés lo mejor para tus hijos. Pero nadie te enseñó a
            manejar esto:
          </p>
          <p className="text-lg md:text-2xl font-bold text-white mt-3 md:mt-4 bg-brand-darkCard py-2 px-4 md:py-3 md:px-6 rounded-xl border border-slate-700" data-id="element-165">
            Un mundo donde los riesgos no están en la calle…{' '}
            <span className="text-blue-400" data-id="element-166">👉 están en el celular.</span>
          </p>
        </div>

        <div className="max-w-3xl mx-auto" data-id="element-167">
          <div className="bg-white shadow-2xl rounded-2xl md:rounded-3xl p-5 md:p-10" data-id="element-168">
            <h3 className="text-lg md:text-xl font-bold mb-5 md:mb-8 text-brand-navy" data-id="element-169">
              Y mientras tanto:
            </h3>
            <ul className="space-y-4 md:space-y-6" data-id="element-170">
              {['Tu hijo pasa horas en redes sin supervisión real', 'No sabés con quién habla', 'No sabés qué contenido consume', 'Pensás que "no pasa nada"… pero no estás seguro', 'Ves noticias de amenazas, bullying y grooming… pero no sabés cómo prevenir'].map((item, i) => <li key={i} className="flex items-start gap-3 md:gap-4" data-id="element-171">
                  <XCircle className="w-5 h-5 md:w-6 md:h-6 text-red-500 shrink-0 mt-0.5" data-id="element-172" />
                  <span className="text-sm md:text-lg text-brand-text" data-id="element-173">
                    {item}
                  </span>
                </li>)}
            </ul>
            <div className="mt-6 md:mt-10 pt-6 md:pt-8 border-t border-zinc-200 text-center space-y-1.5 md:space-y-2" data-id="element-174">
              <p className="text-base md:text-xl font-bold text-brand-navy" data-id="element-175">
                👉 No es falta de interés.
              </p>
              <p className="text-base md:text-xl font-bold text-brand-blue" data-id="element-176">
                👉 Es falta de información clara.
              </p>
            </div>
          </div>
        </div>
      </SectionWrapper>

      <SectionWrapper className="bg-brand-darkAlt border-y border-slate-800" data-id="element-177">
        <div className="text-center mb-8 md:mb-12" data-id="element-178">
          <div className="inline-flex items-center justify-center bg-red-500/15 text-red-500 p-2.5 md:p-3 rounded-full mb-3 md:mb-4" data-id="element-179">
            <AlertOctagon className="w-6 h-6 md:w-8 md:h-8" data-id="element-180" />
          </div>
          <h2 className="text-2xl md:text-4xl font-extrabold text-white" data-id="element-181">
            LO QUE ESTÁ PASANDO HOY
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-6 max-w-4xl mx-auto mb-8 md:mb-12" data-id="element-182">
          {['Amenazas en escuelas', 'Mensajes virales', 'Cuentas falsas', 'Desafíos peligrosos'].map((threat, i) => <div key={i} className="bg-white shadow-lg rounded-xl md:rounded-2xl p-4 md:p-6 text-center flex items-center justify-center h-full" data-id="element-183">
              <span className="font-bold text-sm md:text-base text-brand-navy" data-id="element-184">
                {threat}
              </span>
            </div>)}
        </div>

        <div className="text-center space-y-2 md:space-y-4" data-id="element-185">
          <p className="text-lg md:text-2xl font-bold text-blue-400" data-id="element-186">
            👉 Todo empieza en redes sociales
          </p>
          <p className="text-base md:text-xl text-slate-400" data-id="element-187">
            Y muchos padres… no lo ven.
          </p>
        </div>
      </SectionWrapper>
    </>;
}